import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './NovelGraphView.css';
import { API_BASE_URL } from '../config/api';

// Global variable to immediately block zoom
let GLOBAL_IS_DRAGGING = false;

const MIN_FIT_SCALE = 0.25; // don't auto-fit below this — nodes become unreadable
const MIN_SCALE = 0.025; // hard zoom limits — see wheel/pinch handlers
const MAX_SCALE = 2;
// Forces Chrome to keep this canvas on the CPU. Some GPU drivers composite an
// accelerated canvas layer as a flat white rectangle; software rasterization is
// immune, and this graph is far too simple to need GPU acceleration.
const CANVAS_CTX_OPTS = { willReadFrequently: true, alpha: true };

const NovelGraphView = ({ novel, novelId, isProduction = false }) => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [graphData, setGraphData] = useState({ nodes: [], edges: [] });
  const [selectedNode, setSelectedNode] = useState(null);
  const [viewTransform, setViewTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [renderError, setRenderError] = useState(null);
  const [debugInfo, setDebugInfo] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const skipFirstFitRef = useRef(true);
  const debugInfoRef = useRef('');

  const [isPanning, setIsPanning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const isMouseDownRef = useRef(false);
  const justFinishedDragging = useRef(false);
  const hasCheckedCoordinates = useRef(false);
  const graphContainerRef = useRef(null);
  const onMouseDownRef = useRef(null);
  const onMouseMoveRef = useRef(null);
  const onMouseUpRef = useRef(null);
  // Refs for interaction state — avoids async state lag in rapid touch/mouse events
  const draggedNodeRef = useRef(null);
  const isPanningRef = useRef(false);
  const lastMousePositionRef = useRef({ x: 0, y: 0 });
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const lastPinchDistanceRef = useRef(null);
  // Refs for render-loop state to avoid triggering React re-renders on every frame
  const viewTransformRef = useRef(viewTransform);
  const graphDataRef = useRef(graphData);
  const animFrameRef = useRef(null);
  const needsRedrawRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => { viewTransformRef.current = viewTransform; }, [viewTransform]);
  useEffect(() => { graphDataRef.current = graphData; }, [graphData]);

  // Schedule a canvas redraw via requestAnimationFrame (coalesces multiple updates per frame)
  const requestRedraw = () => {
    if (needsRedrawRef.current) return; // already scheduled
    needsRedrawRef.current = true;
    animFrameRef.current = requestAnimationFrame(() => {
      needsRedrawRef.current = false;
      drawCanvas();
    });
  };


  // Function to save scene coordinates to backend
  const saveSceneCoordinates = async (sceneId, sceneType, x, y) => {
    if (isProduction) return; // Don't allow saving in production mode

    try {
      const token = localStorage.getItem('token');
      const sceneTypePath = sceneType === 'interactive' ? 'interactive_scene'
        : sceneType === 'open_world' ? 'open_world_scene' : 'static_scene';
      const endpoint = `${API_BASE_URL}/building_novel/${novelId}/${sceneTypePath}/${sceneId}/coordinates`;

      await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
        },
        body: JSON.stringify({ x, y }),
      });
    } catch (error) {
      console.error('Failed to save scene coordinates:', error);
    }
  };

  // Function to check and assign coordinates to all scenes that don't have them
  const checkAndAssignMissingCoordinates = async () => {
    if (isProduction || !novel) return; // Don't allow saving in production mode

    const coordinatesToUpdate = [];

    // Reuse the same graph building logic to calculate positions
    const allScenes = new Map();
    if (novel.interactive_scenes) {
      novel.interactive_scenes.forEach(scene => allScenes.set(scene.id, { ...scene, type: 'interactive' }));
    }
    if (novel.static_scenes) {
      novel.static_scenes.forEach(scene => allScenes.set(scene.id, { ...scene, type: 'static' }));
    }
    if (novel.open_world_interactive_scenes) {
      novel.open_world_interactive_scenes.forEach(scene => allScenes.set(scene.id, { ...scene, type: 'open_world' }));
    }

    const visitedScenes = new Set();
    const calculatedPositions = new Map();

    const depthUsedY = new Map();
    const NODE_SPACING = 220;
    const registerDepthY = (depth, y) => {
      if (!depthUsedY.has(depth)) depthUsedY.set(depth, []);
      depthUsedY.get(depth).push(y);
    };
    const findSafeY = (depth, preferredY) => {
      const taken = depthUsedY.get(depth);
      if (!taken || taken.length === 0) {
        registerDepthY(depth, preferredY);
        return preferredY;
      }
      const sorted = [...taken].sort((a, b) => a - b);
      let y = preferredY;
      for (const t of sorted) {
        if (Math.abs(y - t) < NODE_SPACING) {
          y = t + NODE_SPACING;
        }
      }
      registerDepthY(depth, y);
      return y;
    };

    const traverseAndCalculate = (sceneId, sceneType, depth = 0, parentY = 0, siblingIndex = 0, totalSiblings = 1) => {
      const sceneKey = `${sceneId}_${sceneType}`;
      if (visitedScenes.has(sceneKey)) return;
      visitedScenes.add(sceneKey);

      const scene = allScenes.get(sceneId);
      if (!scene) return;

      const x = depth * 350 + 150;
      let y;
      if (Number.isFinite(scene.x) && Number.isFinite(scene.y)) {
        y = scene.y;
        registerDepthY(depth, y);
      } else {
        const preferredY = parentY + (siblingIndex - (totalSiblings - 1) / 2) * 200;
        y = findSafeY(depth, preferredY);
      }

      // Check if scene needs coordinates to be saved
      if (!Number.isFinite(scene.x) || !Number.isFinite(scene.y)) {
        calculatedPositions.set(scene.id, { x, y, type: scene.type });
      }

      // Continue traversal for connected scenes
      let nextScenes = [];
      if ((sceneType === 'interactive' || sceneType === 'open_world') && scene.endings) {
        nextScenes = scene.endings
          .filter(e => e.next_scene?.scene_id)
          .map(e => e.next_scene);
      } else if (sceneType === 'static' && scene.next_scenes) {
        nextScenes = scene.next_scenes.filter(ns => ns.scene_id);
      }

      if ((sceneType === 'interactive' || sceneType === 'open_world') && scene.static_endings) {
        const staticEndingNextScenes = scene.static_endings
          .filter(se => se.next_scene?.scene_id)
          .map(se => se.next_scene);
        nextScenes = [...nextScenes, ...staticEndingNextScenes];
      }

      if (sceneType === 'interactive' && scene.context_ending?.next_scene?.scene_id) {
        nextScenes = [...nextScenes, scene.context_ending.next_scene];
      }

      nextScenes.forEach((nextScene, index) => {
        traverseAndCalculate(nextScene.scene_id, nextScene.scene_type, depth + 1, y, index, nextScenes.length);
      });
    };

    // Start traversal from first scene if it exists
    if (novel.first_scene_id && novel.first_scene_type) {
      traverseAndCalculate(novel.first_scene_id, novel.first_scene_type, 0, 400);
    }

    // Convert calculated positions to update array
    calculatedPositions.forEach((position, sceneId) => {
      coordinatesToUpdate.push({
        scene_id: sceneId,
        scene_type: position.type,
        x: position.x,
        y: position.y
      });
    });

    if (coordinatesToUpdate.length > 0) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_BASE_URL}/building_novel/${novelId}/coordinates/bulk`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-Auth-Token': token,
          },
          body: JSON.stringify({ coordinates: coordinatesToUpdate }),
        });

        // No need to refresh - coordinates will be used on next render
      } catch (error) {
        console.error('Failed to bulk update scene coordinates:', error);
      }
    }
  };

  // Build graph data from novel scenes
  useEffect(() => {
    if (!novel || !novel.first_scene_id) {
      setGraphData({ nodes: [], edges: [] });
      return;
    }

    const nodes = [];
    const edges = [];
    const visitedScenes = new Set();
    const nodePositions = new Map();

    const allScenes = new Map();
    if (novel.interactive_scenes) {
      novel.interactive_scenes.forEach(scene => allScenes.set(scene.id, { ...scene, type: 'interactive' }));
    }
    if (novel.static_scenes) {
      novel.static_scenes.forEach(scene => allScenes.set(scene.id, { ...scene, type: 'static' }));
    }
    if (novel.open_world_interactive_scenes) {
      novel.open_world_interactive_scenes.forEach(scene => allScenes.set(scene.id, { ...scene, type: 'open_world' }));
    }

    // Per-depth occupied Y list — ensures auto-placed scenes never overlap
    const depthUsedY = new Map();
    const NODE_SPACING = 220;
    const registerDepthY = (depth, y) => {
      if (!depthUsedY.has(depth)) depthUsedY.set(depth, []);
      depthUsedY.get(depth).push(y);
    };
    const findSafeY = (depth, preferredY) => {
      const taken = depthUsedY.get(depth);
      if (!taken || taken.length === 0) {
        registerDepthY(depth, preferredY);
        return preferredY;
      }
      // Sort a copy so we always move y forward (ascending) through occupied slots
      const sorted = [...taken].sort((a, b) => a - b);
      let y = preferredY;
      for (const t of sorted) {
        if (Math.abs(y - t) < NODE_SPACING) {
          y = t + NODE_SPACING;
        }
      }
      registerDepthY(depth, y);
      return y;
    };

    const traverseGraph = (sceneId, sceneType, depth = 0, parentY = 0, siblingIndex = 0, totalSiblings = 1) => {
      const sceneKey = `${sceneId}_${sceneType}`;
      if (visitedScenes.has(sceneKey)) return;
      visitedScenes.add(sceneKey);

      const scene = allScenes.get(sceneId);
      if (!scene) return;

      let x, y;
      if (Number.isFinite(scene.x) && Number.isFinite(scene.y)) {
        x = scene.x;
        y = scene.y;
        registerDepthY(depth, y); // reserve stored position so auto-placed siblings avoid it
      } else {
        x = depth * 350 + 150;
        const preferredY = parentY + (siblingIndex - (totalSiblings - 1) / 2) * 200;
        y = findSafeY(depth, preferredY);
      }

      nodePositions.set(sceneKey, { x, y });

      nodes.push({ id: sceneKey, sceneId, name: scene.name, type: sceneType, x, y });

      let nextScenes = [];
      if ((sceneType === 'interactive' || sceneType === 'open_world') && scene.endings) {
        nextScenes = scene.endings
          .filter(e => e.next_scene?.scene_id)
          .map(e => e.next_scene);
      } else if (sceneType === 'static' && scene.next_scenes) {
        nextScenes = scene.next_scenes.filter(ns => ns.scene_id);
      }

      // Also add static endings for interactive/open_world scenes
      if ((sceneType === 'interactive' || sceneType === 'open_world') && scene.static_endings) {
        const staticEndingNextScenes = scene.static_endings
          .filter(se => se.next_scene?.scene_id)
          .map(se => se.next_scene);
        nextScenes = [...nextScenes, ...staticEndingNextScenes];
      }

      // Also add context ending for interactive scenes
      if (sceneType === 'interactive' && scene.context_ending?.next_scene?.scene_id) {
        nextScenes = [...nextScenes, scene.context_ending.next_scene];
      }

      nextScenes.forEach((nextScene, index) => {
        traverseGraph(nextScene.scene_id, nextScene.scene_type, depth + 1, y, index, nextScenes.length);
      });
    };

    traverseGraph(novel.first_scene_id, novel.first_scene_type, 0, 400);

    // After laying out all reachable nodes, create edges for them.
    const laidOutNodeIds = new Set(nodes.map(n => n.id));
    nodes.forEach(node => {
        const scene = allScenes.get(node.sceneId);
        if (!scene) return;

        let nextScenes = [];
        if ((node.type === 'interactive' || node.type === 'open_world') && scene.endings) {
            nextScenes = scene.endings
                .filter(e => e.next_scene?.scene_id)
                .map(e => e.next_scene);
        } else if (node.type === 'static' && scene.next_scenes) {
            nextScenes = scene.next_scenes.filter(ns => ns.scene_id);
        }

        if ((node.type === 'interactive' || node.type === 'open_world') && scene.static_endings) {
            const staticEndingNextScenes = scene.static_endings
                .filter(se => se.next_scene?.scene_id)
                .map(se => se.next_scene);
            nextScenes = [...nextScenes, ...staticEndingNextScenes];
        }

        if (node.type === 'interactive' && scene.context_ending?.next_scene?.scene_id) {
            nextScenes = [...nextScenes, scene.context_ending.next_scene];
        }

        nextScenes.forEach(nextScene => {
            const nextSceneKey = `${nextScene.scene_id}_${nextScene.scene_type}`;
            if (laidOutNodeIds.has(nextSceneKey)) {
                edges.push({ from: node.id, to: nextSceneKey });
            }
        });
    });

    setGraphData({ nodes, edges });
  }, [novel]);

  // Save coordinates for any scenes that don't have them yet (runs on every novel change)
  useEffect(() => {
    if (novel) checkAndAssignMissingCoordinates();
  }, [novel]);

  // Center graph on initial load
  useEffect(() => {
    if (graphData.nodes.length === 0 || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const { width, height } = canvas.getBoundingClientRect();

    const padding = 100;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    graphData.nodes.forEach(node => {
      minX = Math.min(minX, node.x);
      minY = Math.min(minY, node.y);
      maxX = Math.max(maxX, node.x);
      maxY = Math.max(maxY, node.y);
    });

    const graphWidth = maxX - minX + padding;
    const graphHeight = maxY - minY + padding;

    if (graphWidth <= 0 || graphHeight <= 0) return;

    const scaleX = width / graphWidth;
    const scaleY = height / graphHeight;
    // Cap max scale at 1; floor it so huge graphs don't shrink to invisibility
    // (a 77-scene chain otherwise auto-fits at scale 0.016 and renders as nothing).
    let scale = Math.max(MIN_FIT_SCALE, Math.min(scaleX, scaleY, 1));
    if (!Number.isFinite(scale)) scale = 1; // a zero-size canvas yields Infinity here

    const graphCenterX = (minX + maxX) / 2;
    const graphCenterY = (minY + maxY) / 2;

    const x = width / 2 - graphCenterX * scale;
    const y = height / 2 - graphCenterY * scale;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;

    // Don't auto-center the view when dragging or just finished dragging
    if (!isDraggingRef.current && !justFinishedDragging.current) {
      setViewTransform(prev => {
        // Only update if values actually changed to prevent unnecessary re-renders
        if (prev.x === x && prev.y === y && prev.scale === scale) return prev;
        return { x, y, scale };
      });
    }
  }, [graphData]);

  // Canvas drawing function (reads from refs, does NOT depend on React state)
  const drawCanvasInner = () => {
    const canvas = canvasRef.current;
    const currentGraphData = graphDataRef.current;
    const currentViewTransform = viewTransformRef.current;
    if (!canvas || !currentGraphData.nodes.length) return;
    const ctx = canvas.getContext('2d', CANVAS_CTX_OPTS);
    if (!ctx) { setRenderError('Canvas 2D недоступен'); return; }

    const { width, height } = canvas.getBoundingClientRect();

    // A zero-size box means we were measured before layout settled (Telegram Web
    // resizes the mini-app iframe after mount). Retry rather than painting nothing.
    if (!Number.isFinite(width) || !Number.isFinite(height) || width < 1 || height < 1) {
      requestRedraw();
      return;
    }

    // Back the canvas at device resolution, then work in CSS pixels.
    const dpr = window.devicePixelRatio || 1;
    const backingW = Math.round(width * dpr);
    const backingH = Math.round(height * dpr);
    if (canvas.width !== backingW || canvas.height !== backingH) {
      canvas.width = backingW;
      canvas.height = backingH;
    }

    const vt = currentViewTransform;
    if (!Number.isFinite(vt.x) || !Number.isFinite(vt.y) || !Number.isFinite(vt.scale) || vt.scale <= 0) {
      setRenderError('Некорректный масштаб графа');
      return;
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.save();
    ctx.translate(currentViewTransform.x, currentViewTransform.y);
    ctx.scale(currentViewTransform.scale, currentViewTransform.scale);

    // Build a node lookup map for O(1) edge resolution instead of O(n) .find()
    const nodeMap = new Map();
    currentGraphData.nodes.forEach(n => nodeMap.set(n.id, n));

    // Draw edges — bezier curve with gradient + filled arrowhead
    const nodeWidth = 180;
    currentGraphData.edges.forEach(edge => {
      const fromNode = nodeMap.get(edge.from);
      const toNode = nodeMap.get(edge.to);
      if (!fromNode || !toNode) return;

      const startX = fromNode.x + nodeWidth / 2;
      const startY = fromNode.y;
      const endX = toNode.x - nodeWidth / 2;
      const endY = toNode.y;
      const cp1x = startX + 80;
      const cp1y = startY;
      const cp2x = endX - 80;
      const cp2y = endY;

      // Gradient: pale white at start → purple at end
      const grad = ctx.createLinearGradient(startX, startY, endX, endY);
      grad.addColorStop(0, 'rgba(255,255,255,0.35)');
      grad.addColorStop(1, 'rgba(123,47,255,1)');

      ctx.strokeStyle = grad;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, endX, endY);
      ctx.stroke();

      // Filled triangular arrowhead at end (tangent of bezier end)
      const angle = Math.atan2(endY - cp2y, endX - cp2x);
      const arrowLen = 18;
      const arrowAngle = Math.PI / 6;
      ctx.fillStyle = 'rgba(123,47,255,1)';
      ctx.beginPath();
      ctx.moveTo(endX, endY);
      ctx.lineTo(endX - arrowLen * Math.cos(angle - arrowAngle), endY - arrowLen * Math.sin(angle - arrowAngle));
      ctx.lineTo(endX - arrowLen * Math.cos(angle + arrowAngle), endY - arrowLen * Math.sin(angle + arrowAngle));
      ctx.closePath();
      ctx.fill();
    });

    // Draw nodes
    const firstSceneId = novel.first_scene_id;
    currentGraphData.nodes.forEach(node => {
      const isSelected = selectedNode === node.id;
      const isFirst = node.sceneId === firstSceneId;
      const nodeWidth = 180;
      const staticNodeHeight = 60;
      const interactiveNodeHeight = 180; // 3x static height
      const nodeHeight = (node.type === 'interactive' || node.type === 'open_world') ? interactiveNodeHeight : staticNodeHeight;

      ctx.save();
      ctx.translate(node.x - nodeWidth / 2, node.y - nodeHeight / 2);

      // Node shape
      ctx.fillStyle = isSelected ? 'rgba(255,255,255,0.12)' : ((node.type === 'interactive' || node.type === 'open_world') ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)');
      ctx.strokeStyle = isFirst ? '#f6e05e' : (isSelected ? '#63b3ed' : 'rgba(255,255,255,0.08)'); // Off-white border color
      ctx.lineWidth = isFirst || isSelected ? 3 : 2;
      ctx.beginPath();
      if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(0, 0, nodeWidth, nodeHeight, 8);
      } else {
        // Chrome < 99 has no roundRect; an unguarded call throws mid-draw
        const r = 8;
        ctx.moveTo(r, 0);
        ctx.arcTo(nodeWidth, 0, nodeWidth, nodeHeight, r);
        ctx.arcTo(nodeWidth, nodeHeight, 0, nodeHeight, r);
        ctx.arcTo(0, nodeHeight, 0, 0, r);
        ctx.arcTo(0, 0, nodeWidth, 0, r);
        ctx.closePath();
      }
      ctx.fill();
      ctx.stroke();

      // Node content
      ctx.fillStyle = '#E8E8F0'; // Light text color
      ctx.font = 'bold 14px "Inter", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(node.name, nodeWidth / 2, nodeHeight / 2 - 8);

      ctx.fillStyle = 'rgba(255,255,255,0.6)'; // Lighter gray text
      ctx.font = '12px "Inter", sans-serif';
      ctx.fillText(`(${node.type === 'interactive' ? 'Интерактивная' : node.type === 'open_world' ? 'Открытый мир' : 'Статическая'})`, nodeWidth / 2, nodeHeight / 2 + 10);

      ctx.restore();
    });

    ctx.restore();

    // Compact render report — lets a user screenshot the diagnosis without DevTools
    const report = `canvas ${Math.round(width)}x${Math.round(height)} @${dpr}x | scale ${vt.scale.toFixed(3)} | pan ${Math.round(vt.x)},${Math.round(vt.y)} | nodes ${currentGraphData.nodes.length} | edges ${currentGraphData.edges.length}`;
    if (debugInfoRef.current !== report) {
      debugInfoRef.current = report;
      setDebugInfo(report);
    }
  };

  // One-shot diagnostics: explains a blank/white canvas without the user opening DevTools.
  const diagnosedRef = useRef(false);
  const runDiagnostics = (canvas, ctx) => {
    if (diagnosedRef.current) return;
    diagnosedRef.current = true;
    const L = (...a) => console.log('[NovelGraph]', ...a);

    L('canvas attrs', canvas.width, 'x', canvas.height,
      '| css', Math.round(canvas.getBoundingClientRect().width), 'x', Math.round(canvas.getBoundingClientRect().height),
      '| dpr', window.devicePixelRatio);
    L('transform', viewTransformRef.current, '| nodes', graphDataRef.current.nodes.length,
      '| edges', graphDataRef.current.edges.length);
    L('roundRect supported:', typeof ctx.roundRect === 'function');

    // Canvas APIs replaced by an anti-fingerprinting extension / antivirus
    const patched = ['getImageData', 'getContext', 'toDataURL'].filter(fn => {
      const target = fn === 'getContext' || fn === 'toDataURL' ? canvas : ctx;
      return typeof target[fn] === 'function' && !/\[native code\]/.test(Function.prototype.toString.call(target[fn]));
    });
    if (patched.length) console.warn('[NovelGraph] canvas API patched by an extension:', patched);

    // GPU / renderer — a driver bug here is the top suspect for a flat white canvas
    try {
      const gl = document.createElement('canvas').getContext('webgl');
      const dbg = gl && gl.getExtension('WEBGL_debug_renderer_info');
      L('gpu:', dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : (gl ? 'renderer info blocked' : 'WebGL unavailable'));
    } catch (e) { L('gpu: probe failed', e.message); }

    // Read back what was actually painted. Blocked readback is itself a finding.
    try {
      const pts = [[0.25, 0.25], [0.5, 0.5], [0.75, 0.75], [0.5, 0.15], [0.15, 0.5]];
      const px = pts.map(([fx, fy]) => {
        const d = ctx.getImageData(Math.floor(canvas.width * fx), Math.floor(canvas.height * fy), 1, 1).data;
        return [d[0], d[1], d[2], d[3]];
      });
      L('sampled pixels', px);
      const whiteish = px.filter(([r, g, b, a]) => a > 200 && r > 235 && g > 235 && b > 235).length;
      const empty = px.filter(([, , , a]) => a === 0).length;
      if (whiteish >= 4) {
        console.error('[NovelGraph] CANVAS IS WHITE — drawing produced an opaque white surface. ' +
          'Nothing in the draw code paints opaque white, so this is compositing (GPU) or an extension.');
      } else if (empty === px.length) {
        console.error('[NovelGraph] CANVAS IS EMPTY — draw ran but painted nothing. ' +
          'Check the transform and canvas size logged above.');
      } else {
        L('canvas content looks normal — a white screen here is a compositing/GPU issue, not the draw code.');
      }
    } catch (e) {
      console.error('[NovelGraph] getImageData blocked (fingerprint protection?):', e.name, e.message);
    }
  };

  const drawCanvas = () => {
    try {
      drawCanvasInner();
      const canvas = canvasRef.current;
      const ctx = canvas && canvas.getContext('2d', CANVAS_CTX_OPTS);
      if (canvas && ctx && canvas.width > 0) setTimeout(() => runDiagnostics(canvas, ctx), 300);
    } catch (e) {
      console.error('[NovelGraph] draw failed:', e);
      setRenderError(`Ошибка отрисовки: ${e.name}: ${e.message}`);
    }
  };

  // Redraw canvas when React state changes (initial load, node selection, etc.)
  useEffect(() => {
    drawCanvas();
  }, [graphData, selectedNode, viewTransform, novel.first_scene_id]);

  // Redraw whenever the container changes size. Telegram Web resizes the mini-app
  // iframe after mount, so the first measurement can be stale or zero.
  useEffect(() => {
    const container = graphContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => requestRedraw());
    ro.observe(container);
    const onViewportChange = () => requestRedraw();
    window.addEventListener('resize', onViewportChange);
    window.Telegram?.WebApp?.onEvent?.('viewportChanged', onViewportChange);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onViewportChange);
      window.Telegram?.WebApp?.offEvent?.('viewportChanged', onViewportChange);
    };
  }, [graphData.nodes.length]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  const getMousePos = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  };

  const handleMouseDown = (event) => {
    if (event.button !== 0) return;

    isMouseDownRef.current = true;
    const mousePos = getMousePos(event);
    lastMousePositionRef.current = mousePos;

    const vt = viewTransformRef.current;
    const worldX = (mousePos.x - vt.x) / vt.scale;
    const worldY = (mousePos.y - vt.y) / vt.scale;

    const currentNodes = graphDataRef.current.nodes;
    const clickedNode = currentNodes.find(node => {
      const nodeWidth = 180;
      const nodeHeight = (node.type === 'interactive' || node.type === 'open_world') ? 180 : 60;
      return worldX >= node.x - nodeWidth / 2 && worldX <= node.x + nodeWidth / 2 &&
             worldY >= node.y - nodeHeight / 2 && worldY <= node.y + nodeHeight / 2;
    });

    if (clickedNode) {
      const offsetX = worldX - clickedNode.x;
      const offsetY = worldY - clickedNode.y;
      draggedNodeRef.current = clickedNode;
      setDraggedNode(clickedNode);
      dragOffsetRef.current = { x: offsetX, y: offsetY };
      setDragOffset({ x: offsetX, y: offsetY });
    } else {
      isPanningRef.current = true;
      setIsPanning(true);
    }
  };

  const handleMouseUp = async () => {
    const wasMouseDown = isMouseDownRef.current;
    const wasDragging = isDraggingRef.current;
    const currentDraggedNode = draggedNodeRef.current;

    if (wasDragging && currentDraggedNode) {
      await saveSceneCoordinates(currentDraggedNode.sceneId, currentDraggedNode.type, currentDraggedNode.x, currentDraggedNode.y);
    }

    GLOBAL_IS_DRAGGING = false;

    if (wasDragging && currentDraggedNode) {
      justFinishedDragging.current = true;
      setTimeout(() => { justFinishedDragging.current = false; }, 100);
      // Sync ref-only changes back to React state once at end of drag
      setGraphData(graphDataRef.current);
      setViewTransform(viewTransformRef.current);
    } else if (wasMouseDown && currentDraggedNode && !wasDragging) {
      const sceneTypeUrl = currentDraggedNode.type === 'interactive' ? 'interactive_scene' : currentDraggedNode.type === 'open_world' ? 'open_world_scene' : 'static_scene';
      const basePath = isProduction ? 'production_change' : 'building';
      navigate(`/${basePath}/${novelId}/${sceneTypeUrl}/${currentDraggedNode.sceneId}`);
    }

    // Sync panning viewTransform back to React state
    if (isPanningRef.current) {
      setViewTransform(viewTransformRef.current);
    }

    isPanningRef.current = false;
    setIsPanning(false);
    setIsDragging(false);
    isDraggingRef.current = false;
    isMouseDownRef.current = false;
    draggedNodeRef.current = null;
    setDraggedNode(null);
  };

  const handleMouseMove = (event) => {
    const mousePos = getMousePos(event);
    const currentDraggedNode = draggedNodeRef.current;

    // Check if we should start dragging (use refs — no async state lag).
    // Dragging (and thus coordinate saving) is disabled in production; clicks still navigate.
    if (!isDraggingRef.current && currentDraggedNode && isMouseDownRef.current && !isProduction) {
      const deltaX = mousePos.x - lastMousePositionRef.current.x;
      const deltaY = mousePos.y - lastMousePositionRef.current.y;
      if (Math.sqrt(deltaX * deltaX + deltaY * deltaY) > 5) {
        setIsDragging(true);
        isDraggingRef.current = true;
        GLOBAL_IS_DRAGGING = true;
      }
    }

    if (isDraggingRef.current && currentDraggedNode) {
      const vt = viewTransformRef.current;
      const worldX = (mousePos.x - vt.x) / vt.scale;
      const worldY = (mousePos.y - vt.y) / vt.scale;
      const offset = dragOffsetRef.current;
      const newX = worldX - offset.x;
      const newY = worldY - offset.y;

      // Update graphData ref directly for immediate canvas redraw without React re-render
      const currentGraphData = graphDataRef.current;
      graphDataRef.current = {
        ...currentGraphData,
        nodes: currentGraphData.nodes.map(node =>
          node.id === currentDraggedNode.id ? { ...node, x: newX, y: newY } : node
        )
      };

      // Keep ref in sync so handleMouseUp saves the final position
      draggedNodeRef.current = { ...currentDraggedNode, x: newX, y: newY };

      // Schedule a canvas redraw via rAF (coalesces multiple moves per frame)
      requestRedraw();
      lastMousePositionRef.current = mousePos;
      return;
    } else if (isPanningRef.current) {
      const deltaX = mousePos.x - lastMousePositionRef.current.x;
      const deltaY = mousePos.y - lastMousePositionRef.current.y;

      // Update viewTransform ref directly for immediate canvas redraw without React re-render
      const cur = viewTransformRef.current;
      viewTransformRef.current = { ...cur, x: cur.x + deltaX, y: cur.y + deltaY };

      // Schedule a canvas redraw via rAF
      requestRedraw();
    }

    lastMousePositionRef.current = mousePos;
  };


  // Keep handler refs fresh so touch listeners always call the latest version
  onMouseDownRef.current = handleMouseDown;
  onMouseMoveRef.current = handleMouseMove;
  onMouseUpRef.current = handleMouseUp;

  // Zoom functionality - only works when not dragging
  useEffect(() => {
    const handleWheelZoom = (event) => {
      // Block zoom when dragging
      if (GLOBAL_IS_DRAGGING) {
        event.preventDefault();
        return;
      }

      event.preventDefault();

      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      const rawFactor = event.deltaY < 0 ? 1.1 : 0.9;

      setViewTransform(current => {
        // Clamp scale — unbounded zoom makes world-unit line widths explode and
        // the graph saturates to a solid white block.
        const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, current.scale * rawFactor));
        const zoomFactor = scale / current.scale;
        return {
          scale,
          x: mouseX - (mouseX - current.x) * zoomFactor,
          y: mouseY - (mouseY - current.y) * zoomFactor
        };
      });
    };

    // Listen on the graph container, not document — a document-level listener
    // with preventDefault hijacks page scrolling everywhere on the page.
    const container = graphContainerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleWheelZoom, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheelZoom);
    };
  }, [graphData.nodes.length]);


  // Touch support for mobile panning/dragging/pinch-zoom
  // deps: graphData.nodes.length — re-runs when graph mounts (goes from 0→N nodes)
  useEffect(() => {
    const container = graphContainerRef.current;
    if (!container) return;

    const onTouchStart = (e) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        lastPinchDistanceRef.current = null;
        const t = e.touches[0];
        onMouseDownRef.current({ button: 0, clientX: t.clientX, clientY: t.clientY });
      } else if (e.touches.length === 2) {
        onMouseUpRef.current();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastPinchDistanceRef.current = Math.sqrt(dx * dx + dy * dy);
      }
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        lastPinchDistanceRef.current = null;
        const t = e.touches[0];
        onMouseMoveRef.current({ clientX: t.clientX, clientY: t.clientY });
      } else if (e.touches.length === 2 && lastPinchDistanceRef.current !== null) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const zoomFactor = dist / lastPinchDistanceRef.current;
        const rect = container.getBoundingClientRect();
        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
        setViewTransform(cur => {
          const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, cur.scale * zoomFactor));
          const applied = scale / cur.scale; // 1 once clamped, so the view stops instead of drifting
          return {
            scale,
            x: cx - (cx - cur.x) * applied,
            y: cy - (cy - cur.y) * applied,
          };
        });
        lastPinchDistanceRef.current = dist;
      }
    };

    const onTouchEnd = (e) => {
      e.preventDefault();
      lastPinchDistanceRef.current = null;
      if (e.touches.length === 0) {
        onMouseUpRef.current();
      } else if (e.touches.length === 1) {
        const t = e.touches[0];
        onMouseDownRef.current({ button: 0, clientX: t.clientX, clientY: t.clientY });
      }
    };

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
    };
  }, [graphData.nodes.length]); // re-runs when graph first becomes available

  const resetView = () => {
    setRenderError(null);
    // Re-trigger the fit effect by handing it a new graphData identity
    setGraphData(cur => ({ nodes: [...cur.nodes], edges: cur.edges }));
  };

  // Re-fit the graph after the container changes size (entering/leaving fullscreen)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (skipFirstFitRef.current) { skipFirstFitRef.current = false; return; }
    resetView();
  }, [isFullscreen]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', onKey);
    // Prevent the page behind the overlay from scrolling
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isFullscreen]);

  if (!novel) return <div className="graph-loading">Загрузка...</div>;
  if (!novel.first_scene_id) return <div className="graph-no-data"><p>{'Первая сцена не настроена'}</p></div>;
  if (graphData.nodes.length === 0) return <div className="graph-no-data"><p>{'Сцен не найдено'}</p></div>;

  return (
    <div className={`novel-graph-view ${isFullscreen ? 'ngv-fullscreen' : ''}`}>
      <div className="graph-header">
        <h3>{'Граф новеллы'}</h3>
        <p className="graph-help">{'Нажмите на сцену для перехода. Перетаскивайте блоки сцен для их перемещения. Используйте колесо мыши для масштабирования и перетаскивание для панорамирования.'}</p>
        <div className="ngv-header-actions">
          <button type="button" className="ngv-reset-view" onClick={resetView}>{'Сбросить вид'}</button>
          <button type="button" className="ngv-reset-view" onClick={() => setIsFullscreen(f => !f)}>
            {isFullscreen ? 'Свернуть' : 'На весь экран'}
          </button>
        </div>
        {renderError && <p className="ngv-render-error">{renderError}</p>}
        {debugInfo && <p className="ngv-debug">{debugInfo}</p>}
      </div>

      <div
        ref={graphContainerRef}
        className={`graph-container ${isPanning ? 'panning' : ''} ${isDragging ? 'dragging' : ''}`}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseUp}
        onWheel={(e) => {
          if (GLOBAL_IS_DRAGGING) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }}
        style={{ cursor: isDragging ? 'grabbing' : 'default' }}
      >
        <canvas
          ref={canvasRef}
          className="graph-canvas"
          onWheel={(e) => {
            if (GLOBAL_IS_DRAGGING) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
        />
      </div>
    </div>
  );
};

export default NovelGraphView;
